import React, { useState, useEffect } from 'react';
import './SetupWizard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STEPS = [
  { num: 1, title: 'Token Identity', desc: 'Name your token' },
  { num: 2, title: 'Max Supply', desc: 'Set supply cap' },
  { num: 3, title: 'Token Price', desc: 'Initial pricing' },
  { num: 4, title: 'Payment', desc: 'Currency & limits' },
  { num: 5, title: 'Allocation', desc: 'Purchase split' },
  { num: 6, title: 'Level Income', desc: 'Team distribution' },
  { num: 7, title: 'Wallets', desc: 'Wallet addresses' },
  { num: 8, title: 'Admin Account', desc: 'Secure login' },
  { num: 9, title: 'Root User', desc: 'First user' },
  { num: 10, title: 'Complete', desc: 'Launch' },
];

const SetupWizard = ({ onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  // Step 1 — Token Identity
  const [tokenName, setTokenName] = useState('ROBOTOKEN');
  const [tokenSymbol, setTokenSymbol] = useState('ROBO');
  const [tokenDecimals, setTokenDecimals] = useState(18);

  // Step 2 — Max Supply
  const [maxSupply, setMaxSupply] = useState('0');
  const [supplyType, setSupplyType] = useState('unlimited');

  // Step 3 — Token Price
  const [pricePerToken, setPricePerToken] = useState('0.01');

  // Step 4 — Payment Currency
  const [baseCurrency, setBaseCurrency] = useState('BNB');
  const [minBuyUsdt, setMinBuyUsdt] = useState('10');
  const [maxBuyUsdt, setMaxBuyUsdt] = useState('5000');
  const [buyLimitMode, setBuyLimitMode] = useState('unlimited');
  const [buyCapPeriodMonths, setBuyCapPeriodMonths] = useState('6');
  const [sellFeePercent, setSellFeePercent] = useState('10');
  const [maxWithdrawX, setMaxWithdrawX] = useState('2');
  // Withdrawal time-lock removed — window is always 0 (sent for API compatibility)
  const [burnRemainingDays, setBurnRemainingDays] = useState('90');
  const [isP2PEnabled, setIsP2PEnabled] = useState(true);

  // Step 5 — Allocation
  const [alloc, setAlloc] = useState({ payout: 60, team: 15, admin: 10, royalty: 5, burn: 10 });
  const [adminDist, setAdminDist] = useState({ itFund: 20, marketingFund: 20, legalFund: 20, awardFund: 40 });

  // Step 6 — Level Income
  const [levels, setLevels] = useState(10);
  const [levelPct, setLevelPct] = useState([0, 5, 3.5, 2, 1.25, 0.75, 0.5, 0.5, 0.5, 0.5, 0.5]);
  const [levelCrit, setLevelCrit] = useState([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]);

  // Step 7 — Wallets
  const [wallets, setWallets] = useState({ owner: '', teamIncome: '', itFund: '', marketingFund: '', legalFund: '', awardFund: '', royalty: '', liquidity: '', burn: '0x000000000000000000000000000000000000dEaD' });
  // Wallet addresses derived from the private keys in backend .env (the signing wallets)
  const [detected, setDetected] = useState({});

  useEffect(() => {
    const prefill = (det) => {
      setDetected(det);
      setWallets(prev => ({
        ...prev,
        owner: prev.owner || det.main || '',
        teamIncome: prev.teamIncome || det.teamIncome || '',
        liquidity: prev.liquidity || det.liquidity || '',
        royalty: prev.royalty || det.royalty || '',
      }));
    };
    // Preferred: one endpoint returns all signing wallets detected from .env
    fetch(`${API_BASE}/setup/detected-wallets`).then(r => r.json())
      .then(d => {
        if (d && d.success && d.wallets) { prefill(d.wallets); return; }
        throw new Error('no detected-wallets');
      })
      .catch(() => {
        // Fallback: legacy endpoint (main wallet only)
        fetch(`${API_BASE}/setup/main-wallet`).then(r => r.json())
          .then(d => { if (d.success) prefill({ main: d.mainWallet }); }).catch(() => {});
      });
  }, []);

  // Step 8 — Admin Account
  const [adminUser, setAdminUser] = useState('admin');
  const [adminPass, setAdminPass] = useState('');
  const [adminPassConfirm, setAdminPassConfirm] = useState('');
  const [adminName, setAdminName] = useState('Administrator');

  // Step 9 — Root User
  const [rootWallet, setRootWallet] = useState('');

  // Check setup status on load
  useEffect(() => {
    const savedTheme = localStorage.getItem('robo-theme') || 'light';
    document.documentElement.setAttribute('data-theme', savedTheme);

    fetch(`${API_BASE}/setup/status`).then(r => r.json()).then(d => {
      if (d.isSetupComplete) { onComplete && onComplete(); }
      else if (d.currentStep > 0) { setStep(d.currentStep + 1); }
    }).catch(() => {});
  }, [onComplete]);

  const post = async (endpoint, body) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_BASE}/setup/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!d.success) { setError(d.error); setLoading(false); return false; }
      setLoading(false); return true;
    } catch { setError('Connection failed'); setLoading(false); return false; }
  };

  const handleNext = async () => {
    let ok = false;
    switch (step) {
      case 1: ok = await post('step1', { tokenName, tokenSymbol, tokenDecimals }); break;
      case 2: ok = await post('step2', { maxSupply: supplyType === 'unlimited' ? 0 : parseFloat(maxSupply) }); break;
      case 3: ok = await post('step3', { tokensPerUsdt: 1 / parseFloat(pricePerToken) }); break;
      case 4:
        ok = await post('step4', { baseCurrency, minBuyUsdt: parseFloat(minBuyUsdt), maxBuyUsdt: buyLimitMode === 'unlimited' ? 0 : parseFloat(maxBuyUsdt), buyLimitMode, buyCapPeriodMonths: parseInt(buyCapPeriodMonths) || 6, sellFeePercent: parseFloat(sellFeePercent), maxWithdrawMultiplier: parseFloat(maxWithdrawX), withdrawWindowDays: 0, burnRemainingDays: parseInt(burnRemainingDays), isP2PEnabled }); break;
      case 5:
        if (alloc.payout + alloc.team + alloc.admin + alloc.royalty + alloc.burn !== 100) { setError('Must total 100%'); return; }
        if (alloc.admin > 0) {
          const adminTotal = adminDist.itFund + adminDist.marketingFund + adminDist.legalFund + adminDist.awardFund;
          if (adminTotal !== 100) { setError(`Admin distribution must total 100%. Currently: ${adminTotal}%`); return; }
        }
        ok = await post('step5', { payoutPercent: alloc.payout, teamIncomePercent: alloc.team, adminPercent: alloc.admin, royaltyPercent: alloc.royalty, buyBurnPercent: alloc.burn, adminDistribution: adminDist }); break;
      case 6:
        ok = await post('step6', { distributeLevels: levels, levelPercentages: levelPct, levelCriteria: levelCrit }); break;
      case 7: ok = await post('step7', wallets); break;
      case 8:
        if (!adminPass || adminPass.length < 6) { setError('Password must be at least 6 characters'); return; }
        if (adminPass !== adminPassConfirm) { setError('Passwords do not match'); return; }
        ok = await post('step8', { username: adminUser, password: adminPass, name: adminName }); break;
      case 9:
        if (!rootWallet || !rootWallet.startsWith('0x') || rootWallet.length < 42) { setError('Enter a valid wallet address (0x...)'); return; }
        ok = await post('step9', { walletAddress: rootWallet }); break;
      case 10:
        ok = await post('complete', {});
        if (ok) { onComplete && onComplete(); }
        return;
      default: break;
    }
    if (ok) {
      if (step === 9) {
        try {
          const r = await fetch(`${API_BASE}/setup/summary`);
          const d = await r.json();
          if (d.success) setSummary(d.summary);
        } catch {}
      }
      setStep(step + 1);
    }
  };

  const f = (n, d = 4) => n != null ? parseFloat(n).toFixed(d) : '0';
  const allocTotal = alloc.payout + alloc.team + alloc.admin + alloc.royalty + alloc.burn;
  const adminDistTotal = adminDist.itFund + adminDist.marketingFund + adminDist.legalFund + adminDist.awardFund;

  return (
    <div className="setup">
      <div className="setup-container">
        {/* Header */}
        <div className="setup-header">
          <h1 className="setup-logo">◈ ROBO</h1>
          <p className="setup-subtitle">Platform Setup</p>
        </div>

        {/* Progress */}
        <div className="setup-progress">
          {STEPS.map((s) => (
            <div key={s.num} className={`setup-dot ${step >= s.num ? 'active' : ''} ${step === s.num ? 'current' : ''}`}>
              <span className="setup-dot-num">{s.num}</span>
              <span className="setup-dot-label">{s.title}</span>
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="setup-card">
          <h2 className="setup-step-title">{STEPS[step - 1]?.title}</h2>
          <p className="setup-step-desc">{STEPS[step - 1]?.desc}</p>

          {/* STEP 1 — Token Identity */}
          {step === 1 && (
            <div className="setup-fields">
              <div className="setup-field"><label>Token Name</label><input value={tokenName} onChange={e => setTokenName(e.target.value)} className="setup-input" placeholder="e.g. ROBOTOKEN" /></div>
              <div className="setup-field"><label>Token Symbol</label><input value={tokenSymbol} onChange={e => setTokenSymbol(e.target.value.toUpperCase())} className="setup-input" placeholder="e.g. ROBO" maxLength={10} /></div>
              <div className="setup-field"><label>Decimals</label><input type="number" value={tokenDecimals} onChange={e => setTokenDecimals(parseInt(e.target.value))} className="setup-input" /></div>
              <p className="setup-hint">This is how your token appears on MetaMask, exchanges, and wallets.</p>
            </div>
          )}

          {/* STEP 2 — Max Supply */}
          {step === 2 && (
            <div className="setup-fields">
              <div className="setup-radio-group">
                <label className={`setup-radio ${supplyType === 'unlimited' ? 'checked' : ''}`} onClick={() => setSupplyType('unlimited')}>
                  <span className="setup-radio-dot" /><div><strong>Unlimited (Elastic Supply)</strong><p>Tokens minted on demand. No cap. Like BNBConnect.</p></div>
                </label>
                <label className={`setup-radio ${supplyType === 'fixed' ? 'checked' : ''}`} onClick={() => setSupplyType('fixed')}>
                  <span className="setup-radio-dot" /><div><strong>Fixed Max Supply</strong><p>Set a cap. Cannot be changed after setup.</p></div>
                </label>
              </div>
              {supplyType === 'fixed' && (
                <div className="setup-field"><label>Max Supply</label><input type="number" value={maxSupply} onChange={e => setMaxSupply(e.target.value)} className="setup-input" placeholder="e.g. 1000000000" />
                  <p className="setup-hint">⚠️ This CANNOT be changed after setup is complete.</p>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — Token Price */}
          {step === 3 && (
            <div className="setup-fields">
              <div className="setup-field"><label>Price per 1 {tokenSymbol} (in USDT)</label><input type="number" step="0.0001" value={pricePerToken} onChange={e => setPricePerToken(e.target.value)} className="setup-input" placeholder="e.g. 0.01" /></div>
              {pricePerToken > 0 && (
                <div className="setup-preview">
                  1 {tokenSymbol} = {parseFloat(pricePerToken)} USDT<br />
                  For 1 USDT you get {(1 / parseFloat(pricePerToken)).toLocaleString(undefined, { maximumFractionDigits: 2 })} {tokenSymbol}
                </div>
              )}
              <p className="setup-hint">Example: 0.01 means each {tokenSymbol} costs 0.01 USDT. So 1 USDT buys 100 {tokenSymbol}. Price auto-adjusts after buy/sell via formula: price = liquidity ÷ supply</p>
            </div>
          )}

          {/* STEP 4 — Payment Currency & Limits */}
          {step === 4 && (
            <div className="setup-fields">
              <p className="setup-hint">Select the primary currency users will pay with. Live conversion to USDT equivalent is used for min/max checks and token calculation.</p>
              <div className="setup-field">
                <label>Base Payment Currency</label>
                <select value={baseCurrency} onChange={e => setBaseCurrency(e.target.value)} className="setup-input">
                  <option value="BNB">BNB (Binance Coin)</option>
                  <option value="ETH">ETH (Ethereum)</option>
                  <option value="USDT">USDT (Tether)</option>
                  <option value="BTC">BTC (Bitcoin)</option>
                  <option value="MATIC">MATIC (Polygon)</option>
                  <option value="PVC">PVC (PVC Meta)</option>
                </select>
              </div>
              <div className="setup-info-box">
                <strong>How it works:</strong>
                {baseCurrency === 'USDT' ? (
                  <>
                    <p>Users pay directly in USDT — no conversion needed. The limits below are already in USDT.</p>
                    <p>Example: if min is {parseFloat(minBuyUsdt) || 10} USDT, the user needs at least {parseFloat(minBuyUsdt) || 10} USDT.</p>
                  </>
                ) : (
                  <>
                    <p>Users pay in {baseCurrency}. The live {baseCurrency}/USDT rate is fetched from CoinGecko.</p>
                    <p>Min/Max limits are set in USDT equivalent. The user sees how much {baseCurrency} they need.</p>
                    <p>Example: if min is {parseFloat(minBuyUsdt) || 10} USDT and 1 {baseCurrency} = {({ BNB: 600, ETH: 3000, BTC: 60000, MATIC: 0.5, PVC: 1 }[baseCurrency] || 600)} USDT, the user needs min {((parseFloat(minBuyUsdt) || 10) / ({ BNB: 600, ETH: 3000, BTC: 60000, MATIC: 0.5, PVC: 1 }[baseCurrency] || 600)).toFixed(4)} {baseCurrency}.</p>
                  </>
                )}
              </div>
              <div className="setup-field"><label>Minimum Purchase (USDT equivalent)</label><input type="number" value={minBuyUsdt} onChange={e => setMinBuyUsdt(e.target.value)} className="setup-input" /></div>

              <div className="setup-field">
                <label>Buy Limit per User</label>
                <select value={buyLimitMode} onChange={e => setBuyLimitMode(e.target.value)} className="setup-input">
                  <option value="unlimited">Unlimited — users can buy any amount, anytime</option>
                  <option value="limited">Limited — set max per user</option>
                </select>
              </div>
              {buyLimitMode === 'limited' && (
                <>
                  <div className="setup-field"><label>Maximum Purchase per User (USDT equivalent, per period)</label><input type="number" value={maxBuyUsdt} onChange={e => setMaxBuyUsdt(e.target.value)} className="setup-input" /><p className="setup-hint">A user can buy up to this much (USDT equivalent) within each rolling period.</p></div>
                  <div className="setup-field"><label>Cap Period (months)</label><input type="number" value={buyCapPeriodMonths} onChange={e => setBuyCapPeriodMonths(e.target.value)} className="setup-input setup-input-sm" /><p className="setup-hint">Rolling window, anchored at the user's FIRST buy of the period. After {buyCapPeriodMonths || 6} month(s) it resets to a fresh {maxBuyUsdt || 0} USDT — unused allowance does not carry over.</p></div>
                </>
              )}

              <div className="setup-field"><label>Sell Fee % (tokens burned on sell)</label><input type="number" value={sellFeePercent} onChange={e => setSellFeePercent(e.target.value)} className="setup-input setup-input-sm" /><p className="setup-hint">{sellFeePercent}% of tokens burned on every sell. User gets {100 - parseFloat(sellFeePercent || 0)}% value.</p></div>

              <div className="setup-field"><label>Max Withdraw Multiplier</label><input type="number" value={maxWithdrawX} onChange={e => setMaxWithdrawX(e.target.value)} className="setup-input setup-input-sm" /><p className="setup-hint">Cap = {maxWithdrawX}× the user's TOTAL invested amount (all buys combined, cumulative).</p></div>

              <div className="setup-field"><label>Burn Remaining After (days)</label><input type="number" value={burnRemainingDays} onChange={e => setBurnRemainingDays(e.target.value)} className="setup-input setup-input-sm" /><p className="setup-hint">Once the {maxWithdrawX}× cap is used up, leftover tokens are burned {burnRemainingDays} days after the last sell unless the user reinvests.</p></div>

              <div className="setup-info-box">
                <strong>Sell Rules (automatic per user):</strong>
                <p>Cap = {maxWithdrawX}× TOTAL invested (cumulative). A user can sell partially or fully, anytime — <strong>no time lock and no unlock gate</strong> — as long as total withdrawals stay within the cap.</p>
                <p>Specific wallets can be marked <strong>exempt</strong> later from the Admin panel — exempt wallets have no cap and can withdraw any amount, anytime.</p>
              </div>

              <div className="setup-field">
                <label>P2P Transfers</label>
                <select value={isP2PEnabled ? 'yes' : 'no'} onChange={e => setIsP2PEnabled(e.target.value === 'yes')} className="setup-input">
                  <option value="yes">Enabled — users can transfer tokens to each other</option>
                  <option value="no">Disabled</option>
                </select>
              </div>
            </div>
          )}

          {/* STEP 5 — Allocation */}
          {step === 5 && (
            <div className="setup-fields">
              <p className="setup-hint">How each purchase is split. Must total 100%.</p>
              {[['payout', 'Payout to Buyer', 'Tokens minted to buyer'], ['team', 'Team Level Income', 'Distributed to upline in ' + baseCurrency], ['admin', 'Admin Pool', 'Split among admin fund wallets'], ['royalty', 'Royalty Pool', 'Royalty fund in ' + baseCurrency], ['burn', 'Buy Burn', 'Tokens burned (deflationary)']].map(([key, label, desc]) => (
                <div key={key} className="setup-alloc-row">
                  <div className="setup-alloc-info"><strong>{label}</strong><span>{desc}</span></div>
                  <div className="setup-alloc-input"><input type="number" value={alloc[key]} onChange={e => setAlloc({ ...alloc, [key]: parseFloat(e.target.value) || 0 })} className="setup-input setup-input-sm" /><span>%</span></div>
                </div>
              ))}
              <div className={`setup-alloc-total ${allocTotal === 100 ? 'valid' : 'invalid'}`}>Total: {allocTotal}%</div>

              {/* Admin Pool Distribution */}
              {alloc.admin > 0 && (
                <>
                  <div className="setup-section-divider" />
                  <h3 className="setup-sub-heading">Admin Pool Distribution ({alloc.admin}%)</h3>
                  <p className="setup-hint">How the {alloc.admin}% admin allocation is further split among fund wallets. Must total 100%.</p>
                  {[['itFund', 'IT Fund'], ['marketingFund', 'Marketing Fund'], ['legalFund', 'Legal Fund'], ['awardFund', 'Award Fund']].map(([key, label]) => (
                    <div key={key} className="setup-alloc-row">
                      <div className="setup-alloc-info">
                        <strong>{label}</strong>
                        <span>{((alloc.admin * adminDist[key]) / 100).toFixed(2)}% of total purchase</span>
                      </div>
                      <div className="setup-alloc-input"><input type="number" value={adminDist[key]} onChange={e => setAdminDist({ ...adminDist, [key]: parseFloat(e.target.value) || 0 })} className="setup-input setup-input-sm" /><span>%</span></div>
                    </div>
                  ))}
                  <div className={`setup-alloc-total ${adminDistTotal === 100 ? 'valid' : 'invalid'}`}>Admin Split Total: {adminDistTotal}%</div>
                </>
              )}
            </div>
          )}

          {/* STEP 6 — Level Income */}
          {step === 6 && (
            <div className="setup-fields">
              <div className="setup-field"><label>Number of Levels</label><input type="number" value={levels} onChange={e => setLevels(parseInt(e.target.value) || 1)} className="setup-input setup-input-sm" min={1} max={20} /></div>
              <div className="setup-level-grid">
                <div className="setup-level-hdr"><span>Level</span><span>Income %</span></div>
                {Array.from({ length: levels }, (_, i) => i + 1).map(lv => (
                  <div key={lv} className="setup-level-row">
                    <span>L{lv}</span>
                    <input type="number" value={levelPct[lv] || 0} step="0.25" onChange={e => { const p = [...levelPct]; p[lv] = parseFloat(e.target.value) || 0; setLevelPct(p); }} className="setup-input setup-input-sm" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 7 — Wallets */}
          {step === 7 && (
            <div className="setup-fields">

              {/* ─── Operational Wallets ─── */}
              <h3 className="setup-sub-heading">Operational Wallets</h3>
              <p className="setup-hint">Core wallets that handle the purchase flow. The Owner Wallet is where all {baseCurrency} lands first, then gets split to other wallets automatically.</p>

              <div className="setup-field">
                <label>Owner / Main Wallet</label>
                {detected.main && (
                  <div className="setup-info-box">
                    <strong>Detected from .env private key:</strong>
                    <p className="setup-mono">{detected.main}</p>
                    {!wallets.owner && (
                      <button className="setup-btn setup-btn-back" style={{ marginTop: 8 }}
                        onClick={() => setWallets({ ...wallets, owner: detected.main })}>
                        Use This Address
                      </button>
                    )}
                    {wallets.owner && wallets.owner.toLowerCase() !== detected.main.toLowerCase() && (
                      <p style={{ color: '#e74c3c', marginTop: 4 }}>⚠️ Mismatch! This doesn't match the address you entered below.</p>
                    )}
                    {wallets.owner && wallets.owner.toLowerCase() === detected.main.toLowerCase() && (
                      <p style={{ color: '#27ae60', marginTop: 4 }}>✅ Matches</p>
                    )}
                  </div>
                )}
                <input value={wallets.owner} onChange={e => setWallets({ ...wallets, owner: e.target.value })} 
                  className="setup-input setup-input-mono" placeholder="0x..." />
              </div>

              <div className="setup-field">
                <label>Team Income Wallet ({alloc.team}% — holds {baseCurrency} for level claims)</label>
                <input value={wallets.teamIncome} onChange={e => setWallets({ ...wallets, teamIncome: e.target.value })} className="setup-input setup-input-mono" placeholder="0x..." />
                {detected.teamIncome && (
                  <p className="setup-hint" style={{ marginTop: 4 }}><span className="setup-mono">.env: {detected.teamIncome}</span>{' '}
                    {wallets.teamIncome && wallets.teamIncome.toLowerCase() === detected.teamIncome.toLowerCase()
                      ? <span style={{ color: '#27ae60' }}>✅ matches</span> : <span style={{ color: '#e74c3c' }}>⚠️ differs from .env key</span>}</p>
                )}
              </div>

              <div className="setup-field">
                <label>Liquidity Wallet (70% of investment — price backing, pays sell withdrawals)</label>
                <input value={wallets.liquidity} onChange={e => setWallets({ ...wallets, liquidity: e.target.value })} className="setup-input setup-input-mono" placeholder="0x..." />
                {detected.liquidity && (
                  <p className="setup-hint" style={{ marginTop: 4 }}><span className="setup-mono">.env: {detected.liquidity}</span>{' '}
                    {wallets.liquidity && wallets.liquidity.toLowerCase() === detected.liquidity.toLowerCase()
                      ? <span style={{ color: '#27ae60' }}>✅ matches</span> : <span style={{ color: '#e74c3c' }}>⚠️ differs from .env key</span>}</p>
                )}
              </div>

              <div className="setup-field">
                <label>Royalty Wallet ({alloc.royalty}% — pays royalty rewards)</label>
                <input value={wallets.royalty} onChange={e => setWallets({ ...wallets, royalty: e.target.value })} className="setup-input setup-input-mono" placeholder="0x..." />
                {detected.royalty && (
                  <p className="setup-hint" style={{ marginTop: 4 }}><span className="setup-mono">.env: {detected.royalty}</span>{' '}
                    {wallets.royalty && wallets.royalty.toLowerCase() === detected.royalty.toLowerCase()
                      ? <span style={{ color: '#27ae60' }}>✅ matches</span> : <span style={{ color: '#e74c3c' }}>⚠️ differs from .env key</span>}</p>
                )}
              </div>

              <div className="setup-field">
                <label>Burn Wallet (0xdead)</label>
                <input value={wallets.burn} onChange={e => setWallets({ ...wallets, burn: e.target.value })} className="setup-input setup-input-mono" placeholder="0x000...dEaD" />
              </div>

              {/* ─── Admin Fund Wallets ─── */}
              <div className="setup-section-divider" />
              <h3 className="setup-sub-heading">Admin Fund Wallets</h3>
              <p className="setup-hint">The {alloc.admin}% admin allocation is split among these wallets. Configured in Step 5 — IT ({adminDist.itFund}%), Marketing ({adminDist.marketingFund}%), Legal ({adminDist.legalFund}%), Award ({adminDist.awardFund}%).</p>

              {[['itFund', 'IT Fund Wallet', adminDist.itFund], ['marketingFund', 'Marketing Wallet', adminDist.marketingFund], ['legalFund', 'Legal Fund Wallet', adminDist.legalFund], ['awardFund', 'Award Fund Wallet', adminDist.awardFund]].map(([key, label, pct]) => (
                <div key={key} className="setup-field">
                  <label>{label} ({pct}% of {alloc.admin}% = {((alloc.admin * pct) / 100).toFixed(2)}% of total)</label>
                  <input value={wallets[key]} onChange={e => setWallets({ ...wallets, [key]: e.target.value })} className="setup-input setup-input-mono" placeholder="0x..." />
                </div>
              ))}
            </div>
          )}

          {/* STEP 8 — Admin Account */}
          {step === 8 && (
            <div className="setup-fields">
              <p className="setup-hint">Create your admin login. Use a strong password.</p>
              <div className="setup-field"><label>Admin Name</label><input value={adminName} onChange={e => setAdminName(e.target.value)} className="setup-input" /></div>
              <div className="setup-field"><label>Username</label><input value={adminUser} onChange={e => setAdminUser(e.target.value)} className="setup-input" /></div>
              <div className="setup-field"><label>Password</label><input type="password" value={adminPass} onChange={e => setAdminPass(e.target.value)} className="setup-input" placeholder="Min 6 characters" /></div>
              <div className="setup-field"><label>Confirm Password</label><input type="password" value={adminPassConfirm} onChange={e => setAdminPassConfirm(e.target.value)} className="setup-input" /></div>
            </div>
          )}

          {/* STEP 9 — Root User */}
          {step === 9 && (
            <div className="setup-fields">
              <p className="setup-hint">The root user (ROBO000001) is the first user in the referral tree. Typically the project owner's wallet.</p>
              <div className="setup-field">
                <label>Root User Wallet Address</label>
                <input value={rootWallet} onChange={e => setRootWallet(e.target.value.trim())} className="setup-input setup-input-mono" placeholder="0x..." />
              </div>
              <div className="setup-info-box">
                <strong>What happens:</strong>
                <p>User <strong>ROBO000001</strong> created with this wallet. No referrer — top of tree.</p>
                <p>All future users must use this user's code (or a downstream user) as referral.</p>
                <p><strong>Note:</strong> This can be a different wallet than the Owner/Main wallet above. The root user is the first participant — the Owner wallet is the treasury that receives and splits funds.</p>
              </div>
            </div>
          )}

          {/* STEP 10 — Review & Launch */}
          {step === 10 && summary && (
            <div className="setup-fields">
              <p className="setup-hint">Review everything. Once you click Launch, setup is locked forever.</p>
              <div className="setup-summary">
                <div className="setup-summary-row"><span>Token</span><strong>{summary.token.name} ({summary.token.symbol})</strong></div>
                <div className="setup-summary-row"><span>Supply</span><strong>{summary.supply.type}{summary.supply.maxSupply > 0 ? ` — ${summary.supply.maxSupply.toLocaleString()}` : ''}</strong></div>
                <div className="setup-summary-row"><span>Price</span><strong>1 {summary.token.symbol} = {f(summary.price.tokenPriceInUsdt, 4)} USDT (1 USDT = {f(summary.price.tokensPerUsdt, 2)} {summary.token.symbol})</strong></div>
                <div className="setup-summary-row"><span>Currency</span><strong>{summary.payment?.baseCurrency || baseCurrency}</strong></div>
                <div className="setup-summary-row"><span>Buy Limits</span><strong>{summary.payment?.minBuyUsdt || minBuyUsdt} — {summary.payment?.maxBuyUsdt || maxBuyUsdt} USDT eq.</strong></div>
                <div className="setup-summary-row"><span>Sell</span><strong>{summary.payment?.sellFeePercent ?? sellFeePercent}% burn · {summary.payment?.maxWithdrawMultiplier ?? maxWithdrawX}× cap · no time lock · burn-left {summary.payment?.burnRemainingDays ?? burnRemainingDays}d</strong></div>
                <div className="setup-summary-row"><span>Buy Cap</span><strong>{buyLimitMode === 'limited' ? `${maxBuyUsdt} USDT / ${buyCapPeriodMonths} month(s)` : 'Unlimited'}</strong></div>
                <div className="setup-summary-row"><span>Allocation</span><strong>{summary.allocation.payout}% buyer | {summary.allocation.teamIncome}% team | {summary.allocation.admin}% admin | {summary.allocation.royalty}% royalty | {summary.allocation.burn}% burn</strong></div>
                <div className="setup-summary-row"><span>Levels</span><strong>{summary.levels.count} levels</strong></div>
                <div className="setup-summary-row"><span>Root User</span><strong className="setup-mono">{summary.rootUser || rootWallet || '—'}</strong></div>
                <div className="setup-summary-row"><span>Admin</span><strong>{summary.admin?.username}</strong></div>
                <div className="setup-summary-row"><span>Owner Wallet</span><strong className="setup-mono">{summary.wallets?.owner || '—'}</strong></div>
                <div className="setup-summary-row"><span>Team Income</span><strong className="setup-mono">{summary.wallets?.teamIncome || '—'}</strong></div>
              </div>
              <div className="setup-warning">⚠️ Max supply and setup cannot be changed after launch.</div>
            </div>
          )}

          {/* Error */}
          {error && <div className="setup-error">{error}</div>}

          {/* Navigation */}
          <div className="setup-nav">
            {step > 1 && step < 10 && <button className="setup-btn setup-btn-back" onClick={() => { setStep(step - 1); setError(''); }}>← Back</button>}
            <button className="setup-btn setup-btn-next" onClick={handleNext} disabled={loading}>
              {loading ? 'Saving...' : step === 10 ? '🚀 Launch Platform' : 'Next →'}
            </button>
          </div>
        </div>

        <p className="setup-footer">ROBO Platform • Setup Wizard v1.0</p>
      </div>
    </div>
  );
};

export default SetupWizard;